/**
 * Editor IR fetch — GET /api/editor-ir/<jobId>.
 *
 * BACKEND-AUTHORITATIVE LOAD PATH (pdf-editor-no-itext Phase 2a+4). Every editor
 * entry is jobId-keyed (the drag-drop entry registers a transient job — §G-Q1),
 * so the backend `editor-ir` endpoint is the AUTHORITATIVE source of the
 * struct-tree the editor renders and edits (`state.ir`). The in-browser
 * WASM+iText parser was deleted in Phase 4. `loadAuthoritativeIR` is the seam
 * that resolves the authoritative IR + names its provenance (`backend` vs
 * `struct-tree-fallback`), so the load flow reads one typed result instead of
 * open-coding the priority. Per
 * docs/design/pdf-editor-backend-session-retrofit-260715.md §2.2.
 *
 * Public API:
 *   loadAuthoritativeIR(jobId, opts) → Promise<AuthoritativeIRResult | null>
 *     Fetch the backend IR authoritatively. Returns { document, source:
 *     "backend" } on success, null when there is no jobId or the backend
 *     is unreachable.
 *
 *   fetchEditorIR(jobId, opts) → Promise<Document | null>
 *     Low-level backend fetch. Returns the IR Document on success; null on
 *     any failure. `loadAuthoritativeIR` wraps this; `PdfViewAdapter` and the
 *     Tier-2 enrichment path call it directly.
 *
 *   irFromStructList(nodes) → Document
 *     Build a fallback Document from a flat struct-node list (the
 *     `struct-tree-fallback` source of the authoritative load; used only for the
 *     defensive offline / backend-unreachable case).
 *
 * Wire format (editor-overhaul §4.2):
 *   {
 *     title: string|null,
 *     language: string|null,
 *     attestation: object|null,
 *     blocks: [
 *       { kind: "Heading"|"Paragraph"|"Figure"|...,
 *         structId: "S/0/3", page: 2, level: 1,
 *         content: [...inlines], alt?: "...", decorative?: bool,
 *         bbox?: { x, y, w, h },
 *         readingOrderIndex?: number,
 *         ...
 *       },
 *       ...
 *     ]
 *   }
 *
 * @module editor-ir-fetch
 */
import { buildIRFromStructTree } from "./ir.js";
import { appendTokenIfPresent } from "./url-token.js";
export async function fetchEditorIR(jobId, opts = {}) {
    if (!jobId)
        return null;
    if (typeof fetch === "undefined" && !opts.fetchImpl)
        return null;
    const base = opts.baseUrl || "";
    const url = appendTokenIfPresent(`${base}/api/editor-ir/${encodeURIComponent(jobId)}`);
    const doFetch = opts.fetchImpl ?? fetch;
    let resp;
    try {
        resp = await doFetch(url, { signal: opts.signal ?? null, headers: { "Accept": "application/json" } });
    }
    catch {
        // Network error — host will fall back to WASM. Logged at L1 in editor.js.
        return null;
    }
    if (!resp.ok)
        return null;
    let body;
    try {
        body = await resp.json();
    }
    catch {
        // Malformed JSON — host falls back to WASM. The IR-fetch error itself is logged at L1 in editor.js.
        return null;
    }
    if (!body || typeof body !== "object")
        return null;
    // WHY: normalizeIRPayload constructs a Document-shaped object from the wire
    // payload. TS cannot verify this from the untyped Record<string,unknown>
    // return type; double-cast through unknown at this known-safe boundary.
    return (normalizeIRPayload(body));
}
/**
 * Resolve the AUTHORITATIVE struct-tree for the editor load path (Phase 2a).
 *
 * When a `jobId` is present, the backend `editor-ir` endpoint is the source of
 * truth — this returns `{ document, source: "backend" }`. It returns `null` when
 * there is no jobId OR when the backend fetch fails; the caller then uses the
 * defensive struct-tree fallback and records `source: "struct-tree-fallback"`.
 * As of Phase 4 every entry is jobId-keyed (drag-drop registers a transient job),
 * so the happy path is always `"backend"`. Per
 * docs/design/pdf-editor-backend-session-retrofit-260715.md §2.2 / §G-Q1.
 */
export async function loadAuthoritativeIR(jobId, opts = {}) {
    if (!jobId)
        return null;
    const document = await fetchEditorIR(jobId, opts);
    if (document === null)
        return null;
    return { document, source: "backend" };
}
// ─── normalizeIRPayload ───────────────────────────────────────────────────────
/**
 * Tolerate minor IR-shape drift (the server endpoint may not yet emit
 * bbox / readingOrderIndex). Always return an object that the renderer
 * can consume safely (title/language/blocks/attestation present).
 */
export function normalizeIRPayload(body) {
    const out = {
        title: typeof body.title === "string" ? body.title : body.title || null,
        language: typeof body.language === "string" ? body.language : body.language || null,
        attestation: body.attestation ?? null,
        blocks: Array.isArray(body.blocks) ? body.blocks.map(normalizeBlock).filter((b) => b !== null) : [],
        mcids: Array.isArray(body.mcids) ? body.mcids : null,
    };
    return out;
}
// ─── normalizeBlock ───────────────────────────────────────────────────────────
function normalizeBlock(b, i) {
    if (!b || typeof b !== "object")
        return null;
    const out = { ...b };
    if (typeof out.page !== "number")
        out.page = 1;
    if (b.bbox && typeof b.bbox === "object") {
        // Allow {x,y,w,h} or [x,y,w,h]
        if (Array.isArray(b.bbox)) {
            const arr = b.bbox;
            out.bbox = { x: +arr[0] || 0, y: +arr[1] || 0, w: +arr[2] || 0, h: +arr[3] || 0 };
        }
        // else already {x,y,w,h} shape — already copied via spread
    }
    else {
        out.bbox = null;
    }
    if (typeof out.readingOrderIndex !== "number")
        out.readingOrderIndex = i + 1;
    // AdaTool stamp marker — true when the server detected a
    // LongDescriptionInserter stamp on the underlying struct elem (read via
    // PdfAdaToolStampReader). The server emits the field only when true
    // (DefaultIgnoreCondition=WhenWritingNull). Coerce absent / non-boolean
    // input to false so the structural overlay's range-label logic can do
    // `if (next.insertedByDocableLongDescription)` without an existence
    // check. Per docs/design/long-description-policy-audit-260513.md §C.1.1
    // + docs/pdf/robust-action-metadata.md §3.4.
    out.insertedByDocableLongDescription = b.insertedByDocableLongDescription === true;
    return out;
}
// ─── irFromStructList ─────────────────────────────────────────────────────────
/**
 * Build IR from the WASM struct-tree fallback.
 */
export function irFromStructList(nodes) {
    return buildIRFromStructTree(nodes || []);
}
